import React, { forwardRef, useImperativeHandle, useRef } from 'react'
import { Turnstile } from '@marsidev/react-turnstile'
import { getTurnstileSiteKey } from 'util/getTurnstileSiteKey'

/**
 * Shared Cloudflare Turnstile widget. Replaces the legacy Google captcha
 * components (visible v2 and invisible v3).
 *
 * - mode="visible"   -> interactive widget shown on the form (uses VISIBLE_TURNSTILE_SITE_KEY)
 * - mode="invisible" -> runs silently on render, no user interaction (uses INVISIBLE_TURNSTILE_SITE_KEY)
 *
 * The token is delivered via the onToken callback (Cloudflare's canonical onSuccess pattern);
 * we no longer poll getResponse() in a loop. A ref is still exposed (getResponse/reset/execute)
 * for callers that read the token imperatively at submit time.
 */
export const TurnstileWidget = forwardRef(function TurnstileWidget(
  { mode = 'visible', onToken = () => {}, onExpire, style, className },
  ref,
) {
  const innerRef = useRef(null)
  const invisible = mode === 'invisible'

  useImperativeHandle(ref, () => ({
    getResponse: () => innerRef.current?.getResponse(),
    reset: () => innerRef.current?.reset(),
    execute: () => innerRef.current?.execute(),
  }))

  // Turnstile can only run in the browser; Gatsby SSR should render nothing.
  if (typeof window === 'undefined') return null

  return (
    <Turnstile
      ref={innerRef}
      siteKey={getTurnstileSiteKey(mode)}
      onSuccess={token => onToken(token)}
      onError={() => onToken(null)}
      onExpire={() => {
        innerRef.current?.reset()
        onToken(null)
        if (onExpire) onExpire()
      }}
      options={{
        appearance: invisible ? 'interaction-only' : 'always',
        refreshExpired: 'auto',
      }}
      style={
        style || {
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }
      }
      className={className}
    />
  )
})
